import { Badge, HStack, Text } from "@chakra-ui/layout";
import { MenuItem, Tooltip, useDisclosure } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import {
  getCreditFacilityVersionRetrieveQueryKey,
  getCreditFacilityVersionsTableDataRetrieveQueryKey,
  useCreditFacilityVersionRetrieveSuspense,
  useCreditFacilityVersionsTableDataRetrieveSuspense,
  useCreditFacilityVersionUpsert,
} from "gen/endpoints/credit-facility/credit-facility";
import { useFundListSuspense } from "gen/endpoints/fund-entities/fund-entities";
import { useGlobalEntityRetrieveSuspense } from "gen/endpoints/global-entity/global-entity";
import { useLenderListSuspense } from "gen/endpoints/lender/lender";
import { CreditFacilityTypeEnum } from "gen/model/creditFacilityTypeEnum";
import { CreditFacilityVersionData } from "gen/model/creditFacilityVersionData";
import { CreditFacilityVersionUpsertDataRequest } from "gen/model/creditFacilityVersionUpsertDataRequest";
import { EntityFunctionalTypeEnum } from "gen/model/entityFunctionalTypeEnum";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import { VersionStatusEnum } from "gen/model/versionStatusEnum";
import { creditFacilityVersionUpsertBody } from "gen/schemas/credit-facility/credit-facility";
import i18n from "i18n/config";
import React, { ReactElement, useCallback } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { QueryBoundary } from "shared/api/QueryBoundary";
import { Restricted } from "shared/components/access/Restricted";
import {
  ContentBlock,
  ContentBlockActions,
  ContentBlockBody,
  ContentBlockGroup,
  ContentBlockHeader,
  ContentBlockMenu,
  ContentBlockTitle,
} from "shared/components/content/ContentBlock";
import Descriptions from "shared/components/descriptions/Descriptions";
import { FormDrawer } from "shared/components/form/FormDrawer";
import { FormErrorAlert } from "shared/components/form/FormErrorAlert";
import { FormCheckbox } from "shared/components/input/checkbox/Checkbox";
import { FormDateInput } from "shared/components/input/date/DateInput";
import { FormSelect } from "shared/components/input/select/Select";
import { SelectOption } from "shared/components/input/select/SelectOption";
import { FormMoneyInput } from "shared/components/input/text/MoneyInput";
import { FormTextArea } from "shared/components/input/text/TextArea";
import { FormTextInput } from "shared/components/input/text/TextInput";
import { PageTabBody } from "shared/components/layout/PageTabBody";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxCollapse } from "shared/components/table/box/TableBoxCollapse";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { InfoCircleIcon } from "shared/icons/InfoCircleIcon";
import { formatIsoDateToDisplay, nowAsIsoDate } from "shared/utils/DateUtils";
import { formatMoney } from "shared/utils/MoneyUtils";
import { z as zod } from "zod";

export const STATUS_TO_BADGE: Record<
  VersionStatusEnum,
  (effectiveDate?: string) => ReactElement
> = {
  [VersionStatusEnum.completed]: () => (
    <Badge>
      {i18n.t(`enums:VersionStatusEnum.${VersionStatusEnum.completed}`)}
    </Badge>
  ),
  [VersionStatusEnum.current]: () => (
    <Badge colorScheme="primary">
      {i18n.t(`enums:VersionStatusEnum.${VersionStatusEnum.current}`)}
    </Badge>
  ),
  [VersionStatusEnum.scheduled]: (effectiveDate) =>
    effectiveDate ? (
      <Badge colorScheme="warning">
        {i18n.t("enums:VersionStatusEnum.scheduledWithDate", {
          effectiveDate: formatIsoDateToDisplay(effectiveDate),
        })}
      </Badge>
    ) : (
      <Badge colorScheme="warning">
        {i18n.t(`enums:VersionStatusEnum.${VersionStatusEnum.scheduled}`, {
          effectiveDate,
        })}
      </Badge>
    ),
};

export const CreditFacilitySettingsDetail = () => {
  const { t } = useTranslation(["creditFacility", "common", "enums"]);

  const { fundFamilyId, creditFacilityId } = useRequiredStringParams();

  const {
    isOpen: isEditModalOpen,
    onOpen: onEditModalOpen,
    onClose: closeEditModal,
  } = useDisclosure();

  const { data: creditFacility } = useCreditFacilityVersionRetrieveSuspense(
    fundFamilyId,
    creditFacilityId,
  );

  const { data: leadBank } = useGlobalEntityRetrieveSuspense(
    creditFacility.lead_bank_id,
  );

  return (
    <PageTabBody>
      <ContentBlockGroup>
        <ContentBlock>
          <ContentBlockHeader>
            <ContentBlockTitle>
              <HStack>
                <Text textStyle="bodyBold">
                  {t("creditFacility:settings.creditFacilitySettings")}
                </Text>
                {creditFacility.version_status != VersionStatusEnum.completed &&
                  STATUS_TO_BADGE[
                    creditFacility.version_status as VersionStatusEnum
                  ]()}
              </HStack>
            </ContentBlockTitle>
            <Restricted permission={PermissionTypeEnum.config}>
              <ContentBlockActions>
                <ContentBlockMenu>
                  <MenuItem onClick={onEditModalOpen}>
                    {t("common:edit")}
                  </MenuItem>
                </ContentBlockMenu>
              </ContentBlockActions>
            </Restricted>
          </ContentBlockHeader>
          <ContentBlockBody variant="descriptions">
            <Descriptions>
              <Descriptions.Item
                label={t("creditFacility:create.creditFacilityName")}
              >
                {creditFacility.name}
              </Descriptions.Item>
              <Descriptions.Item label={t("creditFacility:create.leadBank")}>
                {leadBank.legal_name}
              </Descriptions.Item>
              <Descriptions.Item
                label={t("creditFacility:create.facilityType")}
              >
                {t(
                  `enums:CreditFacilityTypeEnum.${creditFacility.facility_type}`,
                )}
              </Descriptions.Item>
              <Descriptions.Item
                label={t("creditFacility:create.inceptionDate")}
              >
                {formatIsoDateToDisplay(creditFacility.inception_date)}
              </Descriptions.Item>
              <Descriptions.Item
                label={t("creditFacility:create.maturityDate")}
              >
                {formatIsoDateToDisplay(creditFacility.maturity_date)}
              </Descriptions.Item>
              <Descriptions.Item
                label={t(
                  "creditFacility:create.transactionCodeConfigsAvailableSetting",
                )}
              >
                {creditFacility.transaction_code_configs_available}
              </Descriptions.Item>
              <Descriptions.Item
                label={t("creditFacility:setup.commitments.totalCommitment")}
              >
                {formatMoney(creditFacility.total_commitments)}
              </Descriptions.Item>
              <Descriptions.Item
                label={t(
                  "creditFacility:setup.commitments.entitiesPartyToFacility",
                )}
              >
                {creditFacility.borrowing_entities
                  ?.map((fund) => fund.legal_name)
                  .join(", ")}
              </Descriptions.Item>
            </Descriptions>
          </ContentBlockBody>
        </ContentBlock>
      </ContentBlockGroup>
      <VersionTable />
      {isEditModalOpen && (
        <QueryBoundary>
          <EditDrawer
            creditFacility={creditFacility}
            onCloseComplete={closeEditModal}
          />
        </QueryBoundary>
      )}
    </PageTabBody>
  );
};

const VersionTable = () => {
  const { t } = useTranslation(["creditFacility", "common"]);

  const { fundFamilyId, creditFacilityId } = useRequiredStringParams();

  const { data: versionTableData } =
    useCreditFacilityVersionsTableDataRetrieveSuspense(
      fundFamilyId,
      creditFacilityId,
    );

  return (
    <TableBox>
      <TableBoxHeader>
        <TableBoxTitle textStyle="bodyBold">
          {t("creditFacility:settings.versionHistory")}
        </TableBoxTitle>
        <DataTableBoxActions />
      </TableBoxHeader>
      <TableBoxBody>
        <TableBoxCollapse>
          <DataTable data={versionTableData} />
        </TableBoxCollapse>
      </TableBoxBody>
    </TableBox>
  );
};

interface EditSettingsDrawerProps {
  onCloseComplete: () => void;
  creditFacility: CreditFacilityVersionData;
}

const EditDrawer: React.FC<EditSettingsDrawerProps> = ({
  onCloseComplete,
  creditFacility,
}) => {
  const { t } = useTranslation(["creditFacility", "common", "enums"]);
  const queryClient = useQueryClient();

  const { fundFamilyId } = useRequiredStringParams();

  const { data: entitiesData } = useFundListSuspense({
    fund_family_ids: [fundFamilyId],
    extra_functional_types: [EntityFunctionalTypeEnum.holding_company],
  });

  const form = useForm<CreditFacilityVersionUpsertDataRequest>({
    defaultValues: {
      ...creditFacility,
      id: creditFacility.credit_facility_id,
      reason_for_change: undefined,
      version_date: nowAsIsoDate(),
      borrowing_entity_ids: creditFacility.borrowing_entities?.map(
        ({ id }) => id,
      ) as string[],
    },
    resolver: zodResolver(
      creditFacilityVersionUpsertBody.superRefine((values, ctx) => {
        if (new Date(values.inception_date) >= new Date(values.maturity_date)) {
          ctx.addIssue({
            code: zod.ZodIssueCode.invalid_date,
            message: t("creditFacility:create.maturityDateError"),
            path: ["maturity_date"],
          });
        }
      }),
    ),
  });

  const { mutateAsync: updateCreditFacilityVersion, error: upsertError } =
    useCreditFacilityVersionUpsert();
  const { data: lenderEntitiesData } = useLenderListSuspense();

  const onSubmit = useCallback(
    async (data: CreditFacilityVersionUpsertDataRequest) => {
      await updateCreditFacilityVersion({
        fundFamilyId,
        data,
      });
    },
    [fundFamilyId, updateCreditFacilityVersion],
  );

  const onSuccess = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: getCreditFacilityVersionsTableDataRetrieveQueryKey(
        fundFamilyId,
        creditFacility.credit_facility_id,
      ),
    });
    await queryClient.invalidateQueries({
      queryKey: getCreditFacilityVersionRetrieveQueryKey(
        fundFamilyId,
        creditFacility.credit_facility_id,
      ),
    });
    onCloseComplete();
  }, [onCloseComplete, queryClient, creditFacility, fundFamilyId]);

  const onError = () => true;
  return (
    <FormDrawer
      title={t("creditFacility:settings.edit.title")}
      subtitle={t("creditFacility:settings.edit.subtitle")}
      formProps={{
        form,
        onSubmit,
        onSuccess,
        onError,
      }}
      onCloseComplete={onCloseComplete}
    >
      <FormErrorAlert error={upsertError} mb="16px" />
      <FormTextInput
        name="name"
        title={t("creditFacility:create.creditFacilityName")}
      />
      <FormSelect
        name="facility_type"
        title={t("creditFacility:create.facilityType")}
      >
        {Object.values(CreditFacilityTypeEnum).map((value) => (
          <SelectOption key={value} value={value}>
            {t(`enums:CreditFacilityTypeEnum.${value}`)}
          </SelectOption>
        ))}
      </FormSelect>
      <FormSelect
        name="lead_bank_id"
        title={t("creditFacility:create.leadBank")}
      >
        {lenderEntitiesData.map(({ id, legal_name }) => (
          <SelectOption key={`lender_${id}`} value={id!.toString()}>
            {legal_name}
          </SelectOption>
        ))}
      </FormSelect>
      <FormDateInput
        name="inception_date"
        title={t("creditFacility:create.inceptionDate")}
      />
      <FormDateInput
        name="maturity_date"
        title={t("creditFacility:create.maturityDate")}
      />
      <FormCheckbox name="transaction_code_configs_available" mb="20px">
        <Text>
          {t("creditFacility:create.transactionCodeConfigsAvailable")}
          <Tooltip
            key="tooltip"
            label={t(
              "creditFacility:create.transactionCodeConfigsAvailableTooltip",
            )}
            placement="top"
          >
            <InfoCircleIcon w="12px" h="12px" ml="10px" color="gray.400" />
          </Tooltip>
        </Text>
      </FormCheckbox>
      <FormMoneyInput
        name="total_commitments"
        title={t("creditFacility:setup.commitments.totalCommitment")}
      />
      <FormSelect
        isMulti={true}
        title={t("creditFacility:setup.commitments.entitiesPartyToFacility")}
        name="borrowing_entity_ids"
      >
        {entitiesData.map((entity) => (
          <SelectOption key={entity.id} value={entity.id!}>
            {entity.legal_name}
          </SelectOption>
        ))}
      </FormSelect>
      <Text textStyle="overline" mb="16px">
        {t("creditFacility:settings.edit.facilityDetailsVersion")}
      </Text>
      <FormTextArea
        name="reason_for_change"
        title={t("creditFacility:settings.edit.reasonForChange")}
      />
      <FormDateInput
        name="version_date"
        title={t("creditFacility:settings.edit.versionDate")}
      />
    </FormDrawer>
  );
};
